import { Button, Image } from '@timelyapp/react';
import classNames from 'classnames';
import { Checkmark, Exclamation, Refresh2 } from 'components/Icons';
import Surface from 'components/Surface';
import config from 'config';
import type { Integration } from 'factories/apps';
import React, { useCallback } from 'react';
import styles from './EcosystemRoute.module.scss';

interface IntegrationRowProps {
  integration?: Integration;
  isEdited?: boolean;
  onClick?: (integration: Integration) => void;
}

const IntegrationRow = React.memo<IntegrationRowProps>(({ integration, isEdited, onClick }) => {
  const handleClick = useCallback(() => {
    onClick?.(integration!);
  }, [integration, onClick]);

  if (!integration) return null;

  const hasAppId = !!integration.appId;

  return (
    <li
      data-testid={`integration_apps_${integration.id}`}
      className={classNames(styles.integrationRow, {
        [styles.isEdited]: isEdited,
      })}
      onClick={handleClick}
    >
      <Surface surface={2} className={styles.logo}>
        <Image
          alt={`${integration.displayName} logo`}
          src={integration.logoUrl}
          fallback={config.defaultLogo}
          width="28"
          height="28"
        />
      </Surface>
      <div className={styles.info}>
        <h4>
          {integration.displayName}
          {integration.ui?.isPrerelease && <span className={styles.prerelease}>pre-release</span>}
          {integration.ui?.isBeta && <span className={styles.beta}>beta</span>}
        </h4>
        <p className={integration.description?.endsWith('.') ? undefined : styles.dot}>
          {integration.description}
        </p>
      </div>
      <div className={styles.connectionState}>
        {integration.integrated ? (
          <>
            {integration.ui?.isDisconnected ? (
              <div className={styles.stateContainer}>
                <span className={classNames(styles.stateIcon, styles.warning)}>
                  <Exclamation />
                </span>
                Connection lost
              </div>
            ) : integration.ui?.isImporting ? (
              <div className={styles.stateContainer}>
                <span className={classNames(styles.stateIcon, styles.refresh)}>
                  <Refresh2 />
                </span>
                Sync in progress
              </div>
            ) : (
              <div className={styles.stateContainer}>
                <span className={classNames(styles.stateIcon, styles.checkmark)}>
                  <Checkmark />
                </span>
                Connected
              </div>
            )}
          </>
        ) : (
          <Surface surface={2}>
            <Button size="small">
              {hasAppId ? '+ Connect' : integration.navLink ? 'Go to settings' : 'Get more info'}
            </Button>
          </Surface>
        )}
      </div>
    </li>
  );
});

IntegrationRow.displayName = 'IntegrationRow';
export default IntegrationRow;
